import fetch from 'r/utils/fetch';
import {
  type Credentials,
  type Ticker,
  type Datetime,
  type HistoricalBalance,
  type Timestamp,
  type Strategy,
} from 'r/types';
import { z } from 'zod';

const monthSchema = z.object(
  Object.fromEntries(
    Array.from({ length: 12 }, (_, i) => [String(i), z.nullable(z.number())]),
  ),
);

const GetMonthReturnResponse = z
  .object(
    Object.fromEntries(
      ['2022', '2023', '2024', '2025'].map((year) => [year, monthSchema]),
    ),
  )
  .partial();

const GET = 'GET';
const POST = 'POST';

export const POST_ADMIN_LOGIN = 'POST_ADMIN_LOGIN';
export const GET_ORDER = 'GET_ORDER';
export const GET_BALANCES = 'GET_BALANCES';
export const GET_HISTORICAL_BALANCES = 'GET_HISTORICAL_BALANCES';
export const GET_POSITION = 'GET_POSITION';
export const GET_PRICES = 'GET_PRICES';
export const GET_STRATEGIES = 'GET_STRATEGIES';
export const GET_STATS = 'GET_STATS';
export const GET_CHARTS = 'GET_CHARTS';
export const GET_PRICES_KRAKEN = 'GET_PRICES_KRAKEN';
export const GET_HISTORICAL_BALANCES_BY_PRICES =
  'GET_HISTORICAL_BALANCES_BY_PRICES';

export type GetOrdersResponse = {
  total: number;
  data: Array<{
    id: string;
    actionID: string;
    symbol: string;
    amountOfBaseAsset: string;
    amountOfQuoteAsset: string;
    status: string;
    price: string;
    fillingPrice: string;
    stopLossPrice: string;
    type: string;
    side: 'BUY' | 'SELL';
    realizedPnl: string;
    actionDate: Datetime;
  }>;
};

export type GetPositionResponse = {
  total: number;
  data: Array<
    {
      symbol: Ticker;
      openedAt: Datetime;
      updateDate: Datetime;
      leverage: string;
      entryPrice: string;
      realizedPnl: string;
      unrealizedPnl: string;
      amountOfBaseAsset: string;
      id: string;
      orders: Array<{
        updateDate: Datetime;
        side: 'BUY' | 'SELL';
        amountOfBaseAsset: string;
        amountOfQuoteAsset: string;
        type: 'LIMIT';
        fillingPrice: string;
        commission: string;
        commissionAsset: string;
        symbol: Ticker;
      }>;
    } & (
      | {
          closedAt: Datetime;
          closePrice: string;
        }
      | { closedAt: null; closePrice: '0' }
    )
  >;
};

export type GetHistoricalBalancesResponse = {
  total: number;
  data: HistoricalBalance[];
};

type HistoricalBalanceByPrices = {
  date: string;
  amount: string;
  unrealizedAmount: string;
} & (
  | {
      actionType: 'UNREALISED_PNL_RECALCULATED';
    }
  | {
      actionType: 'TRANSFER';
      amountChange: string;
    }
);

type GetPricesResponse = {
  t: Timestamp[];
  p: string[];
};

type Stats = {
  from: Datetime;
  to: Datetime;
  duration: number;
  startBalance: number;
  endBalance: number;
  totalReturn: number;
  maxDrawdown: number;
  cagr: number;
  sharpe: number;
  sortino: number;
  volatility: number;
  calmar: number;
  omega: number;
  informationRatio: number;
  rSquared: number;
  treynorRatio: number;
  alpha: number;
  beta: number;
};

type Charts = {
  balance: number[];
  profit: number[];
  drawdown: number[];
  exposure: Record<string, Array<number>>;
};

export type GetChartsResponse = {
  timestamp: Timestamp[];
  strategy: Charts;
  benchmark: Charts;
};

type GetStatsResponse = {
  strategy: Stats;
  benchmark: Stats;
  chart: {
    monthReturn: { [key: `"202${number}"`]: { [key: `"${number}"`]: number } };
    benchmarkMonthReturn: {
      [key: `"202${number}"`]: { [key: `"${number}"`]: number };
    };
  } & GetChartsResponse;
};

type GetPricesKrakenResponse = {
  c: Array<string>;
  h: Array<string>;
  l: Array<string>;
  o: Array<string>;
  t: Array<number>;
};

type ResponseMap = {
  [GET_ORDER]: GetOrdersResponse;
  [GET_HISTORICAL_BALANCES_BY_PRICES]: HistoricalBalanceByPrices[];
  [GET_BALANCES]: unknown;
  [GET_POSITION]: GetPositionResponse;
  [GET_HISTORICAL_BALANCES]: GetHistoricalBalancesResponse;
  [POST_ADMIN_LOGIN]: Credentials;
  [GET_PRICES]: GetPricesResponse;
  [GET_PRICES_KRAKEN]: GetPricesKrakenResponse;
  [GET_STATS]: GetStatsResponse;
  [GET_CHARTS]: GetChartsResponse;
  [GET_STRATEGIES]: {
    data: Array<Strategy>;
    total: number;
  };
};

export async function fetchApiRaw<T extends keyof ResponseMap>(
  endpointId: T,
  {
    signal,
    queryParams,
    body,
  }: {
    signal: AbortSignal | undefined;
    queryParams?: { [key: string]: string };
    body?: Array<unknown> | { [key: string]: string };
  },
  accessToken?: string,
) {
  const [method, base] = (() => {
    switch (endpointId) {
      case GET_PRICES_KRAKEN:
        return [GET, 'prices/kraken'];
      case GET_HISTORICAL_BALANCES_BY_PRICES:
        return [GET, 'portfolio/getHistoricalBalancesByPrices'];
      case GET_ORDER:
        return [GET, 'portfolio/order'];
      case GET_BALANCES:
        return [GET, 'portfolio/balances'];
      case GET_POSITION:
        return [GET, 'portfolio/position'];
      case GET_HISTORICAL_BALANCES:
        return [GET, 'portfolio/historicalBalances'];
      case POST_ADMIN_LOGIN:
        return [POST, 'auth/password'];
      case GET_PRICES:
        return [GET, 'prices'];
      case GET_STATS:
        return [GET, 'portfolio/stats'];
      case GET_CHARTS:
        return [GET, 'portfolio/charts'];
      case GET_STRATEGIES:
        return [GET, 'portfolio'];
    }
  })();
  const headers: Headers = new Headers();
  headers.append('Content-Type', 'application/json');
  if (accessToken) {
    headers.append('Authorization', `Bearer ${accessToken}`);
  }
  let url = `${
    (
      endpointId === 'GET_STATS' ||
      endpointId === GET_CHARTS ||
      endpointId === GET_PRICES_KRAKEN ||
      endpointId === 'GET_STRATEGIES' ||
      endpointId === 'POST_ADMIN_LOGIN' ||
      endpointId === 'GET_PRICES' ||
      window.location.href.includes('?source=binance') ||
      window.location.href.includes('?source=mcp') ||
      window.location.href.includes('?source=kraken_spot') ||
      window.location.href.includes('?source=bybit') ||
      window.location.href.includes('?source=spot_api')
    ) ?
      'https://api-strategies.santiment.network/v1'
    : import.meta.env.VITE_BACKEND_URL_API
  }/${base}`;
  if (queryParams) {
    url += `?${Object.entries(queryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')}`;
  }
  const options: RequestInit = { method, headers, signal };
  if (body) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (endpointId === GET_STATS) {
    {
      const result = GetMonthReturnResponse.safeParse(data.chart.monthReturn);
      if (!result.success) {
        result.error.issues.forEach(({ path }) => {
          const [firstKey, ...otherKeys] = path;
          let obj = data[firstKey];
          otherKeys.forEach((key) => (obj = obj[key]));
          // eslint-disable-next-line no-console
          console.error(
            `API schema mismatch: data${path.map((x) => `[${String(x)}]`).join('')}===${obj}`,
          );
        });
        console.log(data); // eslint-disable-line no-console
      }
    }
    {
      const result = GetMonthReturnResponse.safeParse(
        data.chart.benchmarkMonthReturn,
      );
      if (!result.success) {
        result.error.issues.forEach(({ path }) => {
          const [firstKey, ...otherKeys] = path;
          let obj = data[firstKey];
          otherKeys.forEach((key) => (obj = obj[key]));
          // eslint-disable-next-line no-console
          console.error(
            `API schema mismatch: data${path.map((x) => `[${String(x)}]`).join('')}===${obj}`,
          );
        });
        console.log(data); // eslint-disable-line no-console
      }
    }
  }
  return data as ResponseMap[T];
}
